import {useEffect, useMemo, useState} from "react";
import {
    FleetSensorReliabilitySummary,
    HistoricalFleetSensorReliabilitySummary, HistoricalSensorFailureInstance,
    SensorFailureInstance
} from "../../../../autogenerate/api.generated.clients";
import {useHistoricalSensorReliability} from "../../../../hooks/SensorReliability/useHistoricalSensorReliability";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {formatNumberWithCommas, SensorFailureSummary} from "../CurrentSensorReliability/CurrentSensorReliability";
import {DashboardCard} from "../../../../components/DashboardCard/DashboardCard";
import SpeedIcon from "@mui/icons-material/Speed";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./historicalSensorReliability.scss"
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TimelineIcon from "@mui/icons-material/Timeline";
import {Typography} from "@mui/material";
import {DonutChart, DonutData} from "../../../../components/DonutChart/DonutChart";
import {
    BarChart,
    BarPlot, ChartsGrid,
    ChartsTooltip,
    ChartsXAxis, ChartsYAxis,
    LinePlot,
    MarkPlot,
    ResponsiveChartContainer
} from "@mui/x-charts";
import ComboChart from "../../../../components/ComboChart/ComboChart";

interface OldAndNewSensorFailureStackedBarData {
    total: number[];
    newData: number[];
    labels: string[];
}

const sensorStatusAndColorData = [
    {status: 'Valve Cap', color: '#FF6F61', prediction: 'valveCapSensorFailureCount', count: 0},
    {status: 'Cylinder', color: '#6B8E23', prediction: 'cylinderSensorFailureCount', count: 0},
    {status: 'Speed', color: '#4682B4', prediction: 'speedSensorFailureCount', count: 0},
    {status: 'Main Bearing', color: '#D2691E', prediction: 'mainBearingSensorFailureCount', count: 0},
    {status: 'Oil', color: '#8FBC8F', prediction: 'oilSensorFailureCount', count: 0},
    {status: 'Packing Vent', color: '#6495ED', prediction: 'packingVentSensorFailureCount', count: 0},
    {status: 'Thrust Bearing', color: '#CD5C5C', prediction: 'thrustBearingSensorFailureCount', count: 0},
] as DonutData[];

function HistoricalSensorReliability() {
    const [sensorReliabilityData, setSensorReliabilityData] = useState<HistoricalFleetSensorReliabilitySummary | undefined>();
    const [endDate, setEndDate] = useState(useMemo(() => new Date(), []));
    const [startDate, setStartDate] = useState(useMemo(() => {
        const date = new Date();
        date.setMonth(endDate.getMonth() - 12);
        return date;
    }, [endDate]));
    const [datePickerStartDate, setDatePickerStartDate] = useState<Date | undefined>();
    const [datePickerEndDate, setDatePickerEndDate] = useState<Date | undefined>();
    const [sensorFailureSummary, setSensorFailureSummary] = useState<SensorFailureSummary | undefined>();
    const [failedSensorByTypeData, setFailedSensorByTypeData] = useState<DonutData[]>([] as DonutData[]);
    const [failedSensorByMonthData, setFailedSensorByMonthData] = useState<OldAndNewSensorFailureStackedBarData | undefined>();
    const [failedSensorPercentagesByMonthData, setFailedSensorPercentagesByMonthData] = useState<OldAndNewSensorFailureStackedBarData | undefined>();
    const historicalSensorReliabilityData = useHistoricalSensorReliability({startDate: startDate, endDate: endDate});
    
    useEffect(() => {
        if(historicalSensorReliabilityData.data) {
            setSensorReliabilityData(historicalSensorReliabilityData.data);
        }
    }, [historicalSensorReliabilityData.data]);

    useEffect(() => {
        setDatePickerStartDate(startDate);
        setDatePickerEndDate(endDate);
    }, [startDate, endDate]);
    
    useEffect(() => {
        setDefaultValues();
    }, [sensorReliabilityData])
    
    const setDefaultValues = () => {
        if(sensorReliabilityData) {
            setSensorFailureSummary({
                totalSensors: formatNumberWithCommas(sensorReliabilityData.totalSensors),
                failedSensors: formatNumberWithCommas(sensorReliabilityData.failedSensors),
                newFailureCount: formatNumberWithCommas(sensorReliabilityData.newFailureCount),
                failureRate: `${((sensorReliabilityData.failedSensors / sensorReliabilityData.totalSensors) * 100).toFixed(2)}%`,
                isNewFailuresGreaterThanZero: sensorReliabilityData.newFailures !== undefined && sensorReliabilityData.newFailures !== null && sensorReliabilityData.newFailures > 0
            });
            
            setFailedSensorByTypeData(sensorStatusAndColorData.map((item) => (
                {
                    ...item,
                    count: sensorReliabilityData[item.prediction as keyof HistoricalFleetSensorReliabilitySummary] as number || 0
                }
            )));
            
            const sensorFailureInstances = sensorReliabilityData.sensorFailureInstances;
            const failuresByMonth = groupSensorFailuresByMonth(sensorFailureInstances);
            setFailedSensorByMonthData({
                total: failuresByMonth.map((item) => item.totalFailuresCount),
                newData: failuresByMonth.map((item) => item.newFailuresCount),
                labels: failuresByMonth.map((item) => item.month)
            })
            const failurePercentagesByMonth = calculateFailurePercentagesByMonth(sensorFailureInstances, sensorReliabilityData.totalSensors);
            setFailedSensorPercentagesByMonthData({
                total: failurePercentagesByMonth.map((item) => item.totalFailuresPercentage),
                newData: failurePercentagesByMonth.map((item) => item.newFailuresPercentage),
                labels: failurePercentagesByMonth.map((item) => item.month)
            })
        }
    }
    
    return (
        <div className="historical-sensor-reliability-container">
            <div style={{display: 'flex', flexDirection: 'row', gap: '1em'}}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker label={'Start Date'} value={dayjs(datePickerStartDate)} onChange={(value) => setStartDate(value!.toDate())} />
                    <DatePicker label={'End Date'} value={dayjs(datePickerEndDate)} onChange={(value) => setEndDate(value!.toDate())} />
                </LocalizationProvider>
            </div>
            <div className="insight-card-container">
                <DashboardCard label={'Total Sensors'} value={sensorFailureSummary?.totalSensors ?? "null"} isLoading={historicalSensorReliabilityData.isLoading} icon={<SpeedIcon />} />
                <DashboardCard label={'Failed Sensors'} value={sensorFailureSummary?.failedSensors ?? "null"} isLoading={historicalSensorReliabilityData.isLoading} icon={<ErrorOutlineIcon />} />
                <DashboardCard 
                    label={'New Failures'} value={sensorFailureSummary?.newFailureCount ?? "null"} isLoading={historicalSensorReliabilityData.isLoading} 
                    cardClassName={sensorFailureSummary?.isNewFailuresGreaterThanZero ? "new-failure-font-bad" : "new-failure-font-good"}
                    icon={sensorFailureSummary?.isNewFailuresGreaterThanZero ? <TrendingUpIcon /> : <TrendingDownIcon />} />
                <DashboardCard label={'Failure Rate'} value={sensorFailureSummary?.failureRate ?? "null"} isLoading={historicalSensorReliabilityData.isLoading} icon={<TimelineIcon />} />
            </div>   
            <div>
                <Typography variant={"h6"} sx={{paddingBottom: '0.5em'}}>Failed Sensors by Type</Typography>
                <DonutChart data={failedSensorByTypeData} isLoading={historicalSensorReliabilityData.isLoading} showLegend={true} />
            </div>
            <div className={"chart-container"}>
                <div style={{width: '50%'}}>
                    <Typography variant={"h6"} sx={{paddingBottom: '0.5em', paddingTop: '0.5em'}}>Sensors Failed by Month</Typography>
                    <div className={"e-card chart-card"} >
                        <ComboChart
                            data={
                                failedSensorByMonthData ?
                                    [
                                        {data: failedSensorByMonthData.total, label:'Total Failures', type: 'bar', color: '#0072c6'},
                                        {data: failedSensorByMonthData.newData, label:'New Failures', type: 'line', color: 'red'},
                                    ]
                                :
                                    undefined
                            }
                            xAxis={
                                failedSensorByMonthData ?
                                    {data: failedSensorByMonthData.labels, id:'failed-by-month-x-axis-id', label: 'Month'}
                                :
                                    undefined
                            }
                            isLoading={historicalSensorReliabilityData.isLoading}
                        />
                    </div>
                </div>
                <div style={{width: '50%'}}>
                    <Typography variant={"h6"} sx={{paddingBottom: '0.5em', paddingTop: '0.5em'}}>Percent Failed by Month</Typography>
                    <div className={"e-card"} style={{width:'100%', height: '500px'}}>
                        <ComboChart 
                            data={failedSensorPercentagesByMonthData ? 
                                    [
                                        {data: failedSensorPercentagesByMonthData?.total, label:'Total Failures %', type: 'bar', color: '#0072c6'},
                                        {data: failedSensorPercentagesByMonthData?.newData, label:'New Failures %', type: 'line', color: 'red'},
                                    ]
                                :
                                    undefined
                            } 
                            xAxis={failedSensorPercentagesByMonthData ?
                                    {
                                        data: failedSensorPercentagesByMonthData?.labels,
                                        id: 'failed-percentage-by-month-x-axis-id',
                                        label: 'Month',
                                    }
                                :
                                    undefined
                            }
                            isLoading={historicalSensorReliabilityData.isLoading}
                        />
                    </div>
                </div>
            </div>

        </div>
    )
}

export function groupSensorFailuresByMonth(sensorFailureInstances: HistoricalSensorFailureInstance[]): { month: string, newFailuresCount: number, totalFailuresCount: number }[] {
    const failuresByMonth: Record<string, { newFailuresCount: number, totalFailuresCount: number }> = {};

    sensorFailureInstances.forEach(instance => {
        const month = dayjs(instance.eventDate).format('YYYY-MM');
        if (!failuresByMonth[month]) {
            failuresByMonth[month] = { newFailuresCount: 0, totalFailuresCount: 0 };
        }
        if (instance.newFailure) {
            failuresByMonth[month].newFailuresCount++;
        }
        failuresByMonth[month].totalFailuresCount++;
    });

    return Object.entries(failuresByMonth).map(([month, counts]) => ({
        month,
        newFailuresCount: counts.newFailuresCount,
        totalFailuresCount: counts.totalFailuresCount
    }));
}

export function calculateFailurePercentagesByMonth(sensorFailureInstances: HistoricalSensorFailureInstance[], totalSensors: number): { month: string, newFailuresPercentage: number, totalFailuresPercentage: number }[] {
    const failuresByMonth: Record<string, { newFailuresCount: number, totalFailuresCount: number }> = {};
    sensorFailureInstances.forEach(instance => {
        const month = dayjs(instance.eventDate).format('YYYY-MM');
        if (!failuresByMonth[month]) {
            failuresByMonth[month] = { newFailuresCount: 0, totalFailuresCount: 0 };
        }
        if (instance.newFailure) {
            failuresByMonth[month].newFailuresCount++;
        }
        failuresByMonth[month].totalFailuresCount++;
    });

    return Object.entries(failuresByMonth).map(([month, counts]) => ({
        month,
        newFailuresPercentage: Math.round((counts.newFailuresCount / totalSensors) * 10000) / 100,
        totalFailuresPercentage: Math.round((counts.totalFailuresCount / totalSensors) * 10000) / 100
    }));
}
export default HistoricalSensorReliability;